import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    type SortingState,
} from "@tanstack/react-table";
import {
    updateAttribute,
    goEditFlowItem,
    deleteFlowExportReport,
    deleteFlowItem,
    stillSimpleFlow,
    newFlowExportReport,
} from "../../../../actions/flowActions";
import {
    Button,
    Divider,
    TextField,
    FormControl,
    ThemeProvider,
    createTheme,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from "@material-ui/core";
import GenericConfirmModal from "../../../modals/GenericConfirmModal";
import { FlowExportReport } from "../../../../types/stores/flowTypes";
import { IAppState } from "../../../../types/stores";

type Props = {
    flowExport: any;
    canEdit: boolean;
    reports: Array<FlowExportReport>;
};

const FlowExportReportsTable: React.FC<Props> = ({ flowExport, canEdit, reports }: Props) => {
    const dispatch = useDispatch();

    const [isStillSimpleFlow, setIsStillSimpleFlow] = useState(false);
    const [sorting, setSorting] = useState<SortingState>([]);

    const flow = useSelector((state: IAppState) => state.flows.byId[state.selected.flow]);
    const allFlowItems = useSelector((state: IAppState) => state.flowItems.byId);

    useEffect(() => {
        if (flow && flow.FlowBaseType > 0) {
            const isSimpleFlow = Boolean((dispatch as any)(stillSimpleFlow()));
            setIsStillSimpleFlow(isSimpleFlow);
        }
    }, [flow, dispatch]);

    const toggleIsRenaming = flowItemId => {
        dispatch(updateAttribute("flowItems", flowItemId, "isRenaming", !allFlowItems[flowItemId].isRenaming));
    };

    const editReportName = (e, flowItemId) =>
        dispatch(updateAttribute("flowItems", flowItemId, "FlowItemName", e.target.value, true));

    const editNameKeyUp = (e, flowItemId) => {
        if (e.key == "Enter" || e.key == "Escape") {
            editReportName(e, flowItemId);
            dispatch(updateAttribute("flowItems", flowItemId, "isRenaming", false));
            toggleIsRenaming(flowItemId);
        }
    };

    const renderReportName = cellInfo => {
        if (!reports || reports.length <= 0 || reports[cellInfo.tableData.id] == null) return null;
        const item = allFlowItems[reports[cellInfo.tableData.id].FlowItemId];
        if (item == null) {
            return <div>Unknown</div>;
        }

        return (
            <div style={{ display: "flex" }}>
                {item.isRenaming ? (
                    <FormControl style={{ width: "-webkit-fill-available", padding: "5px 15px" }}>
                        <TextField
                            type="text"
                            defaultValue={item.FlowItemName}
                            onBlur={e => editReportName(e, item.FlowItemId)}
                            onKeyUp={e => editNameKeyUp(e, item.FlowItemId)}
                            onMouseDown={e => e.stopPropagation()}
                            inputProps={{ maxLength: 150 }}
                        />
                    </FormControl>
                ) : (
                    <span className="parent-item-label" style={{ width: "-webkit-fill-available" }}>
                        {item.FlowItemName}
                    </span>
                )}
            </div>
        );
    };

    const editReport = id => dispatch(goEditFlowItem(id));

    const deleteReport = (flowExportReportId, flowItemId) => {
        dispatch(deleteFlowExportReport(flowExportReportId));
        dispatch(deleteFlowItem(flowItemId));
    };

    const renderActions = cellInfo => {
        if (!reports || reports.length <= 0 || reports[cellInfo.tableData.id] == null) return null;

        const flowExportReportId = reports[cellInfo.tableData.id].FlowExportReportId;
        const item = allFlowItems[reports[cellInfo.tableData.id].FlowItemId];

        const confirmDeleteBody = <div>{`Are you sure you want to delete this row?`}</div>;
        const confirmDeleteText = "Delete Row";

        return (
            <div style={{ display: "flex" }}>
                <Button
                    className={"edit-button"}
                    onClick={() => toggleIsRenaming(item.FlowItemId)}
                    disabled={!canEdit || (flow && flow.IsLocked)}
                >
                    Rename
                </Button>
                <Button
                    className={"edit-button"}
                    onClick={() => editReport(reports[cellInfo.tableData.id].FlowItemId)}
                    disabled={!canEdit || (flow && flow.IsLocked)}
                >
                    Edit
                </Button>
                <span style={{ position: "relative", top: "5px" }}>
                    <GenericConfirmModal
                        onConfirm={() => deleteReport(flowExportReportId, reports[cellInfo.tableData.id].FlowItemId)}
                        body={confirmDeleteBody}
                        confirmText={confirmDeleteText}
                        title="Confirm Delete"
                        buttonText={
                            <i
                                className="material-icons"
                                style={{
                                    fontSize: "22px",
                                }}
                            >
                                delete_outline
                            </i>
                        }
                        buttonDisabled={!canEdit || (flow && flow.IsLocked)}
                        cnBtnStyle="material-icon-button current-button"
                    />
                </span>
            </div>
        );
    };

    const addExportReport = () => {
        if (flowExport.ExportId != null) {
            dispatch(newFlowExportReport(flowExport.FlowItemId, flowExport.ExportId));
        }
    };

    const columns = useMemo(
        () => [
            {
                id: "reportName",
                header: "Report Name",
                accessorFn: row => allFlowItems[row.FlowItemId]?.FlowItemName || "",
                cell: info => renderReportName({ ...info.row.original, tableData: { id: info.row.index } }),
            },
            {
                id: "actions",
                header: "",
                accessorFn: row => row.FlowExportReportId,
                cell: info => renderActions({ ...info.row.original, tableData: { id: info.row.index } }),
                enableSorting: false,
            },
        ],
        [allFlowItems, reports, canEdit, flow]
    );

    const table = useReactTable({
        data: reports || [],
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    const theme = createTheme({
        overrides: {
            MuiTable: {
                root: {
                    marginTop: "0px !important",
                },
            },
            MuiTableCell: {
                root: {
                    fontSize: "14px",
                },
            },
        },
    });

    return flowExport.ExportId != null && !isStillSimpleFlow ? (
        <>
            <br />
            <br />
            <div className="flow-edit-header">
                <div style={{ flexGrow: 1 }}>
                    <p>Reports</p>
                    <span className="helper-text">Create, edit, and remove reports for this export node</span>
                </div>
                <div className="flow-edit-header-actions">
                    <Button
                        variant="contained"
                        color="secondary"
                        className={"edit-button"}
                        onClick={addExportReport}
                        startIcon={<i className="material-icons">add</i>}
                        disabled={!canEdit || (flow && flow.IsLocked)}
                    >
                        Create Report
                    </Button>
                </div>
            </div>
            <Divider style={{ margin: "20px auto" }} />
            <div className="flow-edit-body">
                <ThemeProvider theme={theme}>
                    <TableContainer style={{ maxHeight: 400, overflowX: "auto", margin: "20px 0px" }}>
                        <Table stickyHeader style={{ tableLayout: "fixed" }}>
                            <TableHead>
                                {table.getHeaderGroups().map(headerGroup => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <TableCell
                                                key={header.id}
                                                style={{
                                                    backgroundColor: "#0075bc",
                                                    color: "#fff",
                                                    fontWeight: 400,
                                                    fontSize: "14px",
                                                    width: header.id === "actions" ? 200 : undefined,
                                                }}
                                            >
                                                {header.isPlaceholder ? null : header.column.getCanSort() ? (
                                                    <TableSortLabel
                                                        style={{ color: "#fff" }}
                                                        active={header.column.getIsSorted() !== false}
                                                        direction={
                                                            header.column.getIsSorted() === "desc" ? "desc" : "asc"
                                                        }
                                                        onClick={header.column.getToggleSortingHandler()}
                                                    >
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </TableSortLabel>
                                                ) : (
                                                    flexRender(header.column.columnDef.header, header.getContext())
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody>
                                {table.getRowModel().rows.map(row => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map(cell => (
                                            <TableCell
                                                key={cell.id}
                                                style={{
                                                    fontSize: "14px",
                                                    padding: "2px",
                                                }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </ThemeProvider>
            </div>
        </>
    ) : null;
};

export default FlowExportReportsTable;
